// Cloudflare R2 Storage Utility for Frontend

const API_BASE = '/api/r2';

interface UploadResponse {
    url: string;
    key: string;
}

interface FileInfo {
    key: string;
    size: number;
    lastModified: Date;
}

interface ProjectMetadata {
    folderStructure: any;
    images: any[];
    annotations: any[];
    createdAt: string;
    updatedAt: string;
}

// Generate a unique project ID based on turbine name and timestamp
export const generateProjectId = (turbineName: string): string => {
    const timestamp = Date.now();
    const sanitized = turbineName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${sanitized}_${timestamp}`;
};

// Get presigned URL for uploading a file
export const getUploadUrl = async (
    projectId: string,
    fileName: string,
    contentType: string
): Promise<UploadResponse> => {
    try {
        const response = await fetch(`${API_BASE}?action=upload&projectId=${projectId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName, contentType }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API hatası (${response.status}): ${errorText}`);
        }

        return response.json();
    } catch (error: any) {
        if (error.message?.includes('API hatası')) {
            throw error;
        }
        throw new Error(`Sunucuya bağlanılamadı: ${error.message || 'Bilinmeyen hata'}`);
    }
};

// Upload a file to R2 using presigned URL (bypasses Vercel size limits)
export const uploadFile = async (
    projectId: string,
    fileName: string,
    file: Blob,
    contentType: string
): Promise<string> => {
    try {
        // Get presigned URL from API
        const { url, key } = await getUploadUrl(projectId, fileName, contentType);

        // Upload directly to R2 using presigned URL
        const uploadResponse = await fetch(url, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': contentType },
        });

        if (!uploadResponse.ok) {
            throw new Error(`R2 yükleme hatası (${uploadResponse.status})`);
        }

        return key;
    } catch (error: any) {
        if (error.message?.includes('hatası')) {
            throw error;
        }
        throw new Error(`R2'ye yükleme başarısız: ${error.message || 'Bilinmeyen hata'}`);
    }
};

// Get presigned URL for downloading a file
export const getDownloadUrl = async (key: string): Promise<string> => {
    const response = await fetch(`${API_BASE}?action=download&key=${encodeURIComponent(key)}`);

    if (!response.ok) {
        throw new Error('Failed to get download URL');
    }

    const data = await response.json();
    return data.url;
};

// List all files in a project
export const listProjectFiles = async (projectId: string): Promise<FileInfo[]> => {
    const response = await fetch(`${API_BASE}?action=list&projectId=${projectId}`);

    if (!response.ok) {
        throw new Error('Failed to list files');
    }

    const data = await response.json();
    return data.files;
};

// Delete a file
export const deleteFile = async (key: string): Promise<void> => {
    const response = await fetch(`${API_BASE}?action=delete&key=${encodeURIComponent(key)}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error('Failed to delete file');
    }
};

// Delete a project
export const deleteProject = async (projectId: string): Promise<void> => {
    const response = await fetch(`${API_BASE}?action=delete-project&projectId=${encodeURIComponent(projectId)}`, {
        method: 'DELETE',
    });

    if (!response.ok) {
        throw new Error('Failed to delete project');
    }
};

// Save project metadata (folder structure, annotations, etc.)
export const saveProjectMetadata = async (
    projectId: string,
    metadata: ProjectMetadata
): Promise<void> => {
    const response = await fetch(`${API_BASE}?action=save-metadata&projectId=${projectId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metadata: { ...metadata, updatedAt: new Date().toISOString() } }),
    });

    if (!response.ok) {
        throw new Error('Failed to save metadata');
    }
};

// Get project metadata
export const getProjectMetadata = async (projectId: string): Promise<ProjectMetadata | null> => {
    const response = await fetch(`${API_BASE}?action=get-metadata&projectId=${projectId}`);

    if (!response.ok) {
        throw new Error('Failed to get metadata');
    }

    const data = await response.json();
    return data.metadata;
};

// List all projects
export const listProjects = async (): Promise<{ id: string }[]> => {
    const response = await fetch(`${API_BASE}?action=list-projects`);

    if (!response.ok) {
        throw new Error('Failed to list projects');
    }

    const data = await response.json();
    return data.projects;
};

// Upload all images from a zip file to R2
export const uploadZipImagesToR2 = async (
    projectId: string,
    images: { path: string; blob: Blob; fileName: string }[],
    onProgress?: (uploaded: number, total: number) => void
): Promise<Map<string, string>> => {
    const keyMap = new Map<string, string>();
    let uploaded = 0;

    for (const image of images) {
        const contentType = image.fileName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
        const key = await uploadFile(projectId, image.path, image.blob, contentType);
        keyMap.set(image.path, key);

        uploaded++;
        if (onProgress) {
            onProgress(uploaded, images.length);
        }
    }

    return keyMap;
};
